/**
 * Cookie consent — single source of truth for GTM/GA4 + Meta Pixel gating.
 *
 * Trackers must not fire until the user explicitly accepts via the cookie
 * banner (CookieConsentBanner.tsx). This module is the only place that
 * should read/write the consent cookie or turn analytics on/off.
 *
 * The cookie name here must stay in sync with the copy of it inlined in
 * public/index.html (the Meta Pixel loader runs before the React bundle,
 * so it can't import this module and needs its own literal).
 */
import { getCookieConsentValue, resetCookieConsentValue } from "react-cookie-consent";
import TagManager from "react-gtm-module";

export const CONSENT_COOKIE_NAME = "giojoy_cookie_consent";

const GTM_ID = "GTM-N9Q5LS54";

export function hasAnalyticsConsent(): boolean {
  return getCookieConsentValue(CONSENT_COOKIE_NAME) === "true";
}

export function hasDeclinedConsent(): boolean {
  return getCookieConsentValue(CONSENT_COOKIE_NAME) === "false";
}

let analyticsGranted = false;

/**
 * Turns on GTM/GA4 + Meta Pixel. Idempotent — safe to call both from
 * index.tsx at boot (returning visitor who already accepted) and from the
 * banner's onAccept handler.
 */
export function grantAnalyticsConsent(): void {
  if (analyticsGranted) return;
  analyticsGranted = true;

  // Discard any dataLayer events pushed by services/analytics.ts helpers
  // before consent was granted, so GTM never retroactively forwards
  // pre-consent activity to GA4 once it initializes.
  window.dataLayer = [];

  TagManager.initialize({ gtmId: GTM_ID });

  window.__grantMetaPixelConsent?.();
}

/**
 * Clears the consent cookie and reloads. GTM and the Meta Pixel can't be
 * cleanly "un-injected" from a live page once loaded, so a reload is the
 * only reliable way to guarantee trackers actually stop and the banner
 * reappears.
 */
export function revokeConsent(): void {
  resetCookieConsentValue(CONSENT_COOKIE_NAME);
  window.location.reload();
}
